import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-54">
    <h3 className="text-2xl font-bold font-serif text-slate-700" data-id="element-55">
      Política de Privacidad
    </h3>
    <p className="text-sm text-slate-400" data-id="element-56">Última actualización: marzo 2026</p>

    <p data-id="element-57">
      En <strong data-id="element-58">Camino a la Luz</strong>, respetamos y protegemos tu
      privacidad. Esta política describe cómo recopilamos, usamos y protegemos
      tu información personal.
    </p>

    <h4 className="text-lg font-semibold text-slate-700 pt-2" data-id="element-59">
      Información que recopilamos
    </h4>
    <p data-id="element-60">
      Cuando realizas una compra, recopilamos la información necesaria para
      procesar tu pedido, incluyendo tu nombre, dirección de correo electrónico
      e información de pago. Esta información es procesada de forma segura a
      través de Shopify.
    </p>

    <h4 className="text-lg font-semibold text-slate-700 pt-2" data-id="element-61">
      Uso de la información
    </h4>
    <p data-id="element-62">Utilizamos tu información únicamente para:</p>
    <ul className="list-disc pl-6 space-y-1" data-id="element-63">
      <li data-id="element-64">Procesar y entregar tu compra</li>
      <li data-id="element-65">Enviarte el ebook digital adquirido</li>
      <li data-id="element-66">Comunicarnos contigo sobre tu pedido</li>
    </ul>

    <h4 className="text-lg font-semibold text-slate-700 pt-2" data-id="element-67">
      Protección de datos
    </h4>
    <p data-id="element-68">
      Tu información personal está protegida mediante encriptación SSL y las
      medidas de seguridad proporcionadas por la plataforma Shopify. No
      vendemos, intercambiamos ni transferimos tu información personal a
      terceros.
    </p>

    <h4 className="text-lg font-semibold text-slate-700 pt-2" data-id="element-69">Cookies</h4>
    <p data-id="element-70">
      Nuestro sitio puede utilizar cookies para mejorar tu experiencia de
      navegación. Puedes desactivar las cookies en la configuración de tu
      navegador.
    </p>

    <h4 className="text-lg font-semibold text-slate-700 pt-2" data-id="element-71">Tus derechos</h4>
    <p data-id="element-72">
      Tienes derecho a acceder, corregir o eliminar tu información personal en
      cualquier momento. Para ejercer estos derechos, puedes comunicarte con
      nosotros.
    </p>
  </div>;
const termsContent = <div className="space-y-4 text-slate-600 leading-relaxed" data-id="element-73">
    <h3 className="text-2xl font-bold font-serif text-slate-700" data-id="element-74">
      Términos y Condiciones
    </h3>
    <p className="text-sm text-slate-400" data-id="element-75">Última actualización: marzo 2026</p>

    <p data-id="element-76">
      Al acceder y utilizar este sitio web y adquirir el ebook{' '}
      <strong data-id="element-77">Camino a la Luz</strong>, aceptas los siguientes términos y
      condiciones.
    </p>

    <h4 className="text-lg font-semibold text-slate-700 pt-2" data-id="element-78">
      Producto digital
    </h4>
    <p data-id="element-79">
      El producto ofrecido es un ebook en formato digital. Una vez completada la
      compra, recibirás acceso inmediato para descargar el archivo. El ebook es
      compatible con dispositivos móviles, tablets y computadoras.
    </p>

    <h4 className="text-lg font-semibold text-slate-700 pt-2" data-id="element-80">Uso personal</h4>
    <p data-id="element-81">
      El ebook es para uso personal únicamente. Queda prohibida la reproducción,
      distribución, modificación o reventa del contenido sin autorización previa
      por escrito.
    </p>

    <h4 className="text-lg font-semibold text-slate-700 pt-2" data-id="element-82">
      Política de reembolso
    </h4>
    <p data-id="element-83">
      Debido a la naturaleza digital del producto, no se aceptan devoluciones
      una vez que el ebook ha sido descargado. Si tienes problemas con la
      descarga, nos comprometemos a ayudarte a resolver cualquier inconveniente
      técnico.
    </p>

    <h4 className="text-lg font-semibold text-slate-700 pt-2" data-id="element-84">
      Descargo de responsabilidad
    </h4>
    <p data-id="element-85">
      El contenido de este ebook tiene fines informativos y educativos. No
      sustituye el consejo, diagnóstico o tratamiento de un profesional de salud
      mental. Si estás experimentando una crisis, te recomendamos buscar ayuda
      profesional de inmediato.
    </p>

    <h4 className="text-lg font-semibold text-slate-700 pt-2" data-id="element-86">
      Modificaciones
    </h4>
    <p data-id="element-87">
      Nos reservamos el derecho de actualizar estos términos en cualquier
      momento. Los cambios serán efectivos inmediatamente después de su
      publicación en este sitio.
    </p>
  </div>;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setActiveModal(null);
    };
    if (activeModal) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [activeModal]);
  return <>
      <footer className="py-12 px-4 bg-[#2F3A3D] border-t border-[#E8E1D5]" data-id="element-88">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6" data-id="element-89">
          <p className="text-sm text-white/60" data-id="element-90">
            © {new Date().getFullYear()} Camino a la Luz. Todos los derechos
            reservados.
          </p>
          <div className="flex items-center gap-8" data-id="element-91">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-white/80 hover:text-[#FFD166] transition-colors focus:outline-none focus:underline" data-id="element-92">
              Política de Privacidad
            </button>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-white/80 hover:text-[#FFD166] transition-colors focus:outline-none focus:underline" data-id="element-93">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      {/* Modal */}
      <AnimatePresence data-id="element-94">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setActiveModal(null)} data-id="element-95">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-[#2F3A3D]/60 backdrop-blur-md" data-id="element-96" />

            {/* Modal Content */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} onClick={e => e.stopPropagation()} className="relative bg-white rounded-3xl shadow-[0_20px_60px_rgba(47,58,61,0.15)] border border-[#E8E1D5] max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8 md:p-12" data-id="element-97">
              <button onClick={() => setActiveModal(null)} className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-[#F7F3E9] text-[#5F6368] hover:bg-[#E8E1D5] hover:text-[#2F3A3D] transition-colors focus:outline-none focus:ring-2 focus:ring-[#F4A261]" aria-label="Cerrar" data-id="element-98">
                <XIcon className="w-5 h-5" data-id="element-99" />
              </button>

              {activeModal === 'privacy' ? privacyContent : termsContent}
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}